import { useMemo, useState, useEffect } from "react";
import { Calendar, ChevronDown, X } from "lucide-react";

// Preset "look back" ranges. `days` is used to compute the from-date; null = all time.
export const RANGE_PRESETS = [
  { key: "all",   label: "All time",       days: null },
  { key: "30d",   label: "Last 30 days",   days: 30 },
  { key: "3m",    label: "Last 3 months",  days: 90 },
  { key: "6m",    label: "Last 6 months",  days: 180 },
  { key: "12m",   label: "Last 12 months", days: 365 },
  { key: "ytd",   label: "This year",      days: "ytd" },
  { key: "custom",label: "Custom range",   days: "custom" },
];

// Compute {from, to} ISO date strings (yyyy-mm-dd) for a preset.
export function rangeFromPreset(key, customFrom, customTo) {
  const now = new Date();
  const toISO = (d) => d.toISOString().slice(0, 10);
  if (key === "all") return { from: null, to: null };
  if (key === "ytd") return { from: `${now.getFullYear()}-01-01`, to: toISO(now) };
  if (key === "custom") return { from: customFrom || null, to: customTo || null };
  const p = RANGE_PRESETS.find((r) => r.key === key);
  if (!p || typeof p.days !== "number") return { from: null, to: null };
  const from = new Date(now);
  from.setDate(from.getDate() - p.days);
  return { from: toISO(from), to: toISO(now) };
}

// Filter an array of orders by received_at within {from, to} inclusive.
export function filterOrdersByRange(orders, from, to) {
  if (!from && !to) return orders;
  return orders.filter((o) => {
    const d = (o.received_at || "").slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });
}

// Small pill-shaped preset selector with an inline "custom range" input pair.
export default function DateRangeFilter({ value, onChange, testidPrefix = "range" }) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(value?.customFrom || "");
  const [customTo, setCustomTo] = useState(value?.customTo || "");
  const currentKey = value?.key || "all";

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (!e.target.closest?.(`[data-daterange-root="${testidPrefix}"]`)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, testidPrefix]);

  const currentLabel = useMemo(() => {
    if (currentKey === "custom" && (customFrom || customTo)) {
      return `${customFrom || "…"} → ${customTo || "…"}`;
    }
    return RANGE_PRESETS.find((r) => r.key === currentKey)?.label || "All time";
  }, [currentKey, customFrom, customTo]);

  const pick = (key) => {
    if (key === "custom") {
      onChange({ key: "custom", customFrom, customTo });
      // keep dropdown open so user can pick dates
      return;
    }
    onChange({ key });
    setOpen(false);
  };

  const applyCustom = () => {
    onChange({ key: "custom", customFrom, customTo });
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    setCustomFrom(""); setCustomTo("");
    onChange({ key: "all" });
  };

  const isFiltered = currentKey !== "all";

  return (
    <div className="relative inline-block" data-daterange-root={testidPrefix}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testidPrefix}-toggle`}
        className={`px-3 py-2 rounded-lg border font-figtree font-medium text-sm flex items-center gap-2 transition-colors duration-200 ${
          isFiltered
            ? "border-forest bg-forest-soft text-forest hover:bg-forest-soft/70"
            : "border-line text-ink-soft hover:bg-sand"
        }`}
      >
        <Calendar className="h-4 w-4" />
        <span>{currentLabel}</span>
        {isFiltered && (
          <span onClick={clear} data-testid={`${testidPrefix}-clear`} className="ml-1 opacity-70 hover:opacity-100" role="button">
            <X className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5 opacity-60" />
      </button>

      {open && (
        <div
          className="absolute z-30 right-0 mt-2 w-72 card-surface p-2 shadow-lg"
          data-testid={`${testidPrefix}-panel`}
        >
          <ul className="max-h-80 overflow-y-auto">
            {RANGE_PRESETS.filter((r) => r.key !== "custom").map((r) => (
              <li key={r.key}>
                <button
                  onClick={() => pick(r.key)}
                  data-testid={`${testidPrefix}-preset-${r.key}`}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-figtree transition-colors duration-200 ${
                    currentKey === r.key ? "bg-forest text-white font-semibold" : "text-ink hover:bg-sand"
                  }`}
                >
                  {r.label}
                </button>
              </li>
            ))}
            <li className="border-t border-line mt-2 pt-2">
              <div className="px-2 pb-1 text-xs font-figtree font-semibold text-ink-soft uppercase tracking-wide">
                Custom range
              </div>
              <div className="px-2 space-y-2">
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  data-testid={`${testidPrefix}-custom-from`}
                  className="w-full h-9 px-2 rounded border border-line outline-none focus:ring-1 focus:ring-forest text-sm"
                  placeholder="From"
                />
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  data-testid={`${testidPrefix}-custom-to`}
                  className="w-full h-9 px-2 rounded border border-line outline-none focus:ring-1 focus:ring-forest text-sm"
                  placeholder="To"
                />
                <button
                  onClick={applyCustom}
                  disabled={!customFrom && !customTo}
                  data-testid={`${testidPrefix}-custom-apply`}
                  className="w-full py-1.5 rounded-md bg-forest text-white text-sm font-figtree font-semibold hover:bg-forest-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
